/**
 * ClientPrepGuideTab — Prep-Guide for Client Portal (Intake → Prep-Guide)
 *
 * Re-homed from staff-only DocsGuidePage to client-facing portal.
 * Token-gated, not Clerk-gated.
 *
 * Design: CSS variables only, dark default, Daylight via data-theme.
 * No gold, teal, blue, purple, or red signal colors.
 *
 * @see docs/plans/redesign-plan §5.7/§5.11
 */

import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FileText,
  CreditCard,
  Receipt,
  FileCheck,
  Calculator,
  Building2,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Sparkles,
  BookOpen,
  Shield,
  BarChart3,
  Upload,
} from 'lucide-react';
import { BRAND_NAME } from '@/config/brand';

export default function ClientPrepGuideTab() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const primaryDocs = [
    {
      icon: FileText,
      name: 'Tax Returns (Form 1065, K-1)',
      description:
        "State and federal tax returns from the last 2-3 years are crucial for verifying income and providing a historical overview of your business's financial position.",
      tips: [
        "Schedule K-1 shows partner's share of income",
        'Form 1125-A details Cost of Goods Sold',
        'Provides annual revenue baseline',
      ],
    },
    {
      icon: CreditCard,
      name: 'Bank Statements',
      description:
        'Bank statements provide an objective look at cash flows and can be used to trace revenues and expenses month-by-month.',
      tips: [
        'Include all business accounts',
        '12-24 months of history recommended',
        'Our AI extracts and categorizes transactions',
      ],
    },
    {
      icon: Receipt,
      name: 'Sales & Revenue Reports',
      description:
        'Daily or monthly revenue records, POS reports, and customer orders help demonstrate consistent patterns and income drop during interruption.',
      tips: [
        'POS system exports are ideal',
        'Shows seasonal patterns',
        'Helps establish "but-for" baseline',
      ],
    },
  ];

  const secondaryDocs = [
    {
      icon: FileCheck,
      name: 'Payroll Records',
      description:
        'Employee wage logs help support claims for continuing expenses, especially if essential staff remained on payroll.',
    },
    {
      icon: FileText,
      name: 'Invoices & Contracts',
      description:
        'Supplier contracts, customer orders, and cancelled contracts prove lost opportunities and ongoing expenses.',
    },
    {
      icon: Building2,
      name: 'Inventory Records',
      description:
        'Inventory lists and supply chain documents show business volume before and after interruption.',
    },
    {
      icon: Calculator,
      name: 'Budgets & Forecasts',
      description:
        'Pre-loss budgets and financial projections support estimates of expected earnings.',
    },
    {
      icon: Receipt,
      name: 'Utility Bills & Expenses',
      description:
        'Utility bills, lease payments, and expense records demonstrate continuing operating costs.',
    },
  ];

  const challenges = [
    {
      title: 'Risk of Under-Valuation',
      description:
        'Without standard P&Ls, insurers may argue losses are overstated. Our system addresses this by validating against industry benchmarks.',
      solution: 'BLS-validated industry comparisons',
    },
    {
      title: 'Establishing "But-For" Baseline',
      description:
        'Proving what revenue would have been requires historical trend analysis when monthly data is missing.',
      solution: 'Prophet-based forecasting with BLS integration',
    },
    {
      title: 'Identifying Saved vs. Continuing Expenses',
      description:
        'Insurers may incorrectly categorize continuing expenses as "saved" to reduce payouts.',
      solution: 'AI-powered expense classification',
    },
    {
      title: 'Extended Investigation Delays',
      description:
        'Incomplete records can slow down claims processing significantly.',
      solution: 'Comprehensive document package with automated validation',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="p-3 bg-[var(--color-needs-human-tint)] border border-[var(--color-needs-human-tint-bd)] rounded-xl">
          <BookOpen className="w-8 h-8 text-[var(--color-brand)]" />
        </div>
        <div>
          <h1
            className="text-2xl font-bold text-[var(--color-text-primary)]"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
          >
            Documentation Guide
          </h1>
          <p className="text-[var(--color-text-secondary)] mt-1">
            Everything you need to know about filing a business interruption claim with or
            without P&amp;L statements.
          </p>
        </div>
      </div>

      {/* Key Message */}
      <div className="bg-[var(--color-needs-human-tint)] border border-[var(--color-needs-human-tint-bd)] rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-[var(--color-brand)] rounded-xl">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2
              className="text-lg font-bold text-[var(--color-text-primary)] mb-2"
              style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}
            >
              No P&amp;L? No Problem.
            </h2>
            <p className="text-[var(--color-text-secondary)]">
              If monthly Profit &amp; Loss statements are unavailable, you can still file a business
              interruption claim. Our AI-powered system can{' '}
              <strong className="text-[var(--color-text-primary)]">reconstruct your P&amp;L</strong>{' '}
              from bank statements, tax returns, and other financial records—validated against
              industry benchmarks for maximum credibility.
            </p>
          </div>
        </div>
      </div>

      {/* Primary Documents */}
      <section>
        <h2
          className="text-xl font-bold text-[var(--color-text-primary)] mb-4 flex items-center gap-2"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
        >
          <CheckCircle className="w-5 h-5 text-[var(--color-done)]" />
          Primary Alternative Documents
        </h2>
        <p className="text-[var(--color-text-secondary)] mb-4">
          These documents are most effective for reconstructing your financial position:
        </p>

        <div className="space-y-4">
          {primaryDocs.map((doc, i) => (
            <div
              key={i}
              className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl p-5 hover:border-[var(--color-brand)] transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[var(--color-done)]/10 border border-[var(--color-done)]/30 rounded-xl">
                  <doc.icon className="w-5 h-5 text-[var(--color-done)]" />
                </div>
                <div className="flex-1">
                  <h3
                    className="font-semibold text-[var(--color-text-primary)] mb-1"
                    style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}
                  >
                    {doc.name}
                  </h3>
                  <p className="text-[var(--color-text-secondary)] text-sm mb-3">{doc.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {doc.tips.map((tip, j) => (
                      <span
                        key={j}
                        className="text-xs bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] px-2 py-1 rounded-full border border-[var(--color-border)]"
                      >
                        {tip}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Secondary Documents */}
      <section>
        <h2
          className="text-xl font-bold text-[var(--color-text-primary)] mb-4 flex items-center gap-2"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
        >
          <FileText className="w-5 h-5 text-[var(--color-brand)]" />
          Supporting Documents
        </h2>
        <p className="text-[var(--color-text-secondary)] mb-4">
          These additional documents strengthen your claim:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {secondaryDocs.map((doc, i) => (
            <div
              key={i}
              className="bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-lg p-4"
            >
              <div className="flex items-start gap-3">
                <doc.icon className="w-5 h-5 text-[var(--color-text-muted)] mt-0.5" />
                <div>
                  <h4
                    className="font-medium text-[var(--color-text-primary)] text-sm"
                    style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}
                  >
                    {doc.name}
                  </h4>
                  <p className="text-xs text-[var(--color-text-secondary)] mt-1">{doc.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How Reconstruction Works */}
      <section>
        <h2
          className="text-xl font-bold text-[var(--color-text-primary)] mb-4 flex items-center gap-2"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
        >
          <TrendingUp className="w-5 h-5 text-[var(--color-brand)]" />
          How P&amp;L Reconstruction Works
        </h2>

        <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { num: '1', title: 'Upload', desc: 'Upload bank statements, tax returns, and other records' },
              { num: '2', title: 'Extract', desc: 'AI extracts transactions and financial data automatically' },
              { num: '3', title: 'Categorize', desc: 'Transactions classified to expense categories using AI' },
              { num: '4', title: 'Validate', desc: 'Results compared against BLS industry benchmarks' },
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-[var(--color-brand)] rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-white">{step.num}</span>
                </div>
                <h4
                  className="font-semibold text-[var(--color-text-primary)] mb-1"
                  style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}
                >
                  {step.title}
                </h4>
                <p className="text-xs text-[var(--color-text-secondary)]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Benchmarks */}
      <section>
        <h2
          className="text-xl font-bold text-[var(--color-text-primary)] mb-4 flex items-center gap-2"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
        >
          <BarChart3 className="w-5 h-5 text-[var(--color-brand)]" />
          Industry Benchmark Validation
        </h2>

        <div className="bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-xl p-5">
          <div className="flex items-start gap-4">
            <Shield className="w-6 h-6 text-[var(--color-brand)] flex-shrink-0" />
            <div>
              <h3
                className="font-semibold text-[var(--color-text-primary)] mb-2"
                style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}
              >
                The "Yardstick Method"
              </h3>
              <p className="text-sm text-[var(--color-text-secondary)] mb-3">
                When your business lacks historical P&amp;L data, we use the Yardstick Method—comparing
                your reconstructed financials against industry averages from the Bureau of Labor
                Statistics (BLS).
              </p>
              <ul className="text-sm text-[var(--color-text-secondary)] space-y-1">
                <li>• COGS ratios compared to NAICS industry averages</li>
                <li>• Expense categories validated against sector norms</li>
                <li>• Profit margins benchmarked for reasonableness</li>
                <li>• Adds credibility for insurers and adjusters</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Common Challenges */}
      <section>
        <h2
          className="text-xl font-bold text-[var(--color-text-primary)] mb-4 flex items-center gap-2"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
        >
          <AlertCircle className="w-5 h-5 text-[var(--color-brand)]" />
          Challenges Without P&amp;Ls (And How We Solve Them)
        </h2>

        <div className="space-y-3">
          {challenges.map((challenge, i) => (
            <div
              key={i}
              className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h4
                    className="font-medium text-[var(--color-text-primary)]"
                    style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}
                  >
                    {challenge.title}
                  </h4>
                  <p className="text-sm text-[var(--color-text-secondary)] mt-1">{challenge.description}</p>
                </div>
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center gap-1 text-xs bg-[var(--color-done)]/10 text-[var(--color-done)] px-2 py-1 rounded-full border border-[var(--color-done)]/30">
                    <CheckCircle className="w-3 h-3" />
                    {challenge.solution}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <div className="bg-[var(--color-bg-surface)] border border-[var(--color-brand)] rounded-2xl p-8 text-center">
        <h2
          className="text-2xl font-bold text-[var(--color-text-primary)] mb-3"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
        >
          Ready to Upload Your Documents?
        </h2>
        <p className="text-[var(--color-text-secondary)] mb-6 max-w-lg mx-auto">
          Whether you have P&amp;L statements or need to use alternative documentation, our AI-powered
          system will guide you through the process.
        </p>
        <Link
          to={token ? `/client/upload?token=${token}` : '/client/upload'}
          className="inline-flex items-center gap-2 bg-[var(--color-brand)] hover:bg-[var(--color-brand)]/90 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}
        >
          <Upload className="w-5 h-5" />
          Go to Upload
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </div>
  );
}
